import type { CSSProperties } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  normalizeSelectorPlatform,
  SelectorPlatformIcon,
} from "./selector-platform-icon";
import type { SelectorAccount } from "./types";

export type SelectorAvatarSize = 16 | 20 | 24;

const avatarSizeClassNames: Record<SelectorAvatarSize, string> = {
  16: "size-4",
  20: "size-5",
  24: "size-6",
};

const avatarTextClassNames: Record<SelectorAvatarSize, string> = {
  16: "text-[7px]",
  20: "text-[8px]",
  24: "text-[9px]",
};

const platformBadgeClassNames: Record<SelectorAvatarSize, string> = {
  16: "!size-2",
  20: "!size-2",
  24: "!size-2.5",
};

const attentionBadgeClassNames: Record<SelectorAvatarSize, string> = {
  16: "size-1.5",
  20: "size-2",
  24: "size-2",
};

function initials(label: string) {
  return (
    label
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

export function SelectorAvatar({
  account,
  size = 20,
  className,
  style,
}: {
  account: SelectorAccount;
  size?: SelectorAvatarSize;
  className?: string;
  style?: CSSProperties;
}) {
  const platform = normalizeSelectorPlatform(account.platformType);
  const sizeClassName = avatarSizeClassNames[size];
  const attentionReason = account.disabledReason || account.attentionReason;
  const avatarTitle = attentionReason
    ? `${account.label}. ${attentionReason}`
    : account.label;

  if (account.leading) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full [&>*]:max-h-full [&>*]:max-w-full",
          sizeClassName,
          className
        )}
        data-selector-avatar=""
        data-size={size}
        style={style}
      >
        {account.leading}
      </span>
    );
  }

  return (
    <span
      className={cn("relative inline-flex shrink-0", sizeClassName, className)}
      title={avatarTitle}
      data-selector-avatar=""
      data-size={size}
      style={
        {
          "--selector-avatar-color": account.color || "#40C79A",
          ...style,
        } as CSSProperties
      }
    >
      <Avatar
        className={cn(
          "border-2 border-[var(--selector-avatar-color)] bg-white font-medium text-neutral-700",
          sizeClassName,
          avatarTextClassNames[size]
        )}
      >
        {account.avatarUrl ? (
          <AvatarImage
            src={account.avatarUrl}
            alt=""
            referrerPolicy="no-referrer"
          />
        ) : null}
        <AvatarFallback
          className={cn(
            "bg-[var(--selector-avatar-color)] font-medium text-white",
            avatarTextClassNames[size]
          )}
        >
          {initials(account.label)}
        </AvatarFallback>
      </Avatar>
      {account.platformType ? (
        <SelectorPlatformIcon
          altText=""
          type={platform}
          className={cn(
            "absolute -right-px -bottom-px",
            platformBadgeClassNames[size]
          )}
        />
      ) : null}
      {account.disconnected ? (
        <span
          aria-label={
            attentionReason
              ? `${account.label}: Account needs attention: ${attentionReason}`
              : `${account.label}: Account needs attention`
          }
          title={avatarTitle}
          className={cn(
            "absolute -left-0.5 -top-0.5 rounded-full border border-white bg-red-500",
            attentionBadgeClassNames[size]
          )}
        />
      ) : null}
    </span>
  );
}

export function SelectorAvatarStack({
  accounts,
  max = 4,
  size = 20,
  className,
}: {
  accounts: SelectorAccount[];
  max?: number;
  size?: SelectorAvatarSize;
  className?: string;
}) {
  const visibleAccounts = accounts.slice(0, max);
  const remaining = Math.max(0, accounts.length - max);

  return (
    <span
      className={cn("inline-flex shrink-0 items-center gap-1", className)}
      data-selector-avatar-stack=""
      data-size={size}
    >
      <span className="isolate inline-flex shrink-0 items-center">
        {visibleAccounts.map((account, index) => (
          <SelectorAvatar
            key={account.id}
            account={account}
            size={size}
            className={cn(index > 0 && "-ml-1")}
            style={{ zIndex: visibleAccounts.length - index }}
          />
        ))}
      </span>
      {remaining > 0 ? (
        <span
          className={cn(
            "inline-flex items-center justify-center rounded-full bg-neutral-700 font-inter font-medium leading-[14px] text-white",
            avatarSizeClassNames[size],
            size === 20 ? "text-[10px]" : avatarTextClassNames[size]
          )}
          aria-label={`${remaining} more accounts`}
          title={`${remaining} more accounts`}
        >
          +{Math.min(remaining, 99)}
        </span>
      ) : null}
    </span>
  );
}
